import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { resolveRetentionFlowErrorView } from '../RetentionOfferModal.functions';
import { tRetentionOfferErrorModalProps } from '../RetentionOfferModal.types';

/**
 * A retention step that did not complete: the offer lookup failed, or the
 * accept was refused or went unconfirmed. It never reads as a success, and it
 * only offers the ways out that can still work for the refusal it shows.
 */
export default function RetentionOfferErrorModal(
  props: tRetentionOfferErrorModalProps,
) {
  const {
    isOpen,
    error,
    onClose,
    onRetry,
    onContinueToCancellation,
    isLoading,
  } = props;
  const { t } = useTranslation('settings/SubscriptionManager');

  const view = resolveRetentionFlowErrorView(error);

  return (
    <Modal open={isOpen} className="max-w-[631px] rounded-lg p-0">
      <Modal.Header className="flex flex-row justify-end p-4 pb-0 mb-0">
        <Button
          color="ghost"
          size="sm"
          shape="circle"
          onClick={onClose}
          disabled={isLoading}
          aria-label={t('cancelSubscription.retentionOfferError.closeButton')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Button>
      </Modal.Header>
      <Modal.Body className="pt-0 px-6 pb-6 font-sans">
        <div className="flex flex-col gap-6 items-center">
          {/* Error Icon */}
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-error/10">
            <ExclamationTriangleIcon className="w-8 h-8 text-error" />
          </div>

          {/* Heading */}
          <div className="flex flex-col gap-2 text-center">
            <h4 className="text-[28px] font-bold text-error">
              {t('cancelSubscription.retentionOfferError.heading')}
            </h4>
            <p className="text-base text-neutral-700" role="alert">
              {t(view.messageKey)}
            </p>
          </div>

          <div className="flex flex-col gap-2 items-center w-full">
            {view.canRetry ? (
              <Button
                color="primary"
                className="w-full rounded-[4px]"
                onClick={onRetry}
                disabled={isLoading}
                data-testid="retention-offer-error-retry-button"
              >
                {t('cancelSubscription.retentionOfferError.retryButton')}
              </Button>
            ) : (
              <Button
                color="primary"
                className="w-full rounded-[4px]"
                onClick={onClose}
                disabled={isLoading}
                data-testid="retention-offer-error-close-button"
              >
                {t('cancelSubscription.retentionOfferError.closeButton')}
              </Button>
            )}

            {view.canContinueToCancellation && (
              <Button
                color="ghost"
                variant="link"
                size="sm"
                className="underline p-0 text-neutral-700"
                onClick={onContinueToCancellation}
                loading={isLoading}
                disabled={isLoading}
                data-testid="retention-offer-error-continue-button"
              >
                {t(
                  'cancelSubscription.retentionOfferError.continueToCancellationButton',
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
